'use client'

import React from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ArrowLeft, Download, FileDoc, MapPin, Envelope, LinkedinLogo, Phone, Medal, Briefcase, GraduationCap, Calendar } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'
import { CV_ATS_DOCX_URL, CV_PDF_DOWNLOAD_NAME, CV_PDF_URL } from '@/utils/downloadCv'

export default function Resume() {
  return (
    <>
      {/* Screen + print styles for Resume CV */}
      <style dangerouslySetInnerHTML={{
        __html: `
          [data-print-only] { display: none !important; }

          @media print {
            @page {
              margin: 0.6in 0.65in;
              size: letter;
            }

            html, body {
              background: #fff !important;
              color: #141414 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            nav,
            .print\\:hidden,
            [data-print-hide] {
              display: none !important;
            }

            [data-print-only] {
              display: block !important;
            }

            ul[data-print-only],
            ol[data-print-only] {
              display: block !important;
            }

            li[data-print-only] {
              display: list-item !important;
            }

            [data-resume-content] {
              display: block !important;
              background: #fff !important;
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              margin: 0 !important;
              color: #141414 !important;
              max-width: none !important;
            }

            main {
              padding: 0 !important;
              background: #fff !important;
              min-height: 0 !important;
            }

            main > div {
              max-width: none !important;
              margin: 0 !important;
            }

            h1 {
              font-size: 18pt !important;
              margin: 0 0 4pt !important;
              color: #141414 !important;
            }

            h2 {
              font-size: 11pt !important;
              margin: 0 0 8pt !important;
              color: #141414 !important;
              font-weight: 600 !important;
            }

            h3 {
              font-size: 12pt !important;
              margin: 14pt 0 6pt !important;
              padding-bottom: 2pt !important;
              border-bottom: 1px solid #141414 !important;
              color: #141414 !important;
            }

            h4 {
              font-size: 10.5pt !important;
              margin: 0 0 2pt !important;
              color: #141414 !important;
            }

            p, li {
              font-size: 9.5pt !important;
              line-height: 1.35 !important;
              color: #141414 !important;
            }

            section {
              margin-bottom: 10pt !important;
              break-inside: auto;
              page-break-inside: auto;
            }

            [data-resume-job],
            [data-resume-block] {
              break-inside: avoid;
              page-break-inside: avoid;
              margin-bottom: 8pt !important;
              border-left: 2px solid #141414 !important;
              padding-left: 10pt !important;
            }

            ul {
              margin: 4pt 0 0 !important;
              padding-left: 14pt !important;
            }

            li {
              margin-bottom: 2pt !important;
            }

            a {
              color: #141414 !important;
              text-decoration: none !important;
            }
          }
        `
      }} />
      
      <Components.SkipLink />
      
      <Components.PageContainer className="min-h-screen min-h-[100dvh] print:min-h-0">
      <div className="print:hidden">
        <Components.GlassNavigation />
      </div>
      
      <main id="main-content" tabIndex={-1} className="pt-20 md:pt-32 pb-8 md:pb-16 px-4 md:px-6 min-h-screen min-h-[100dvh] print:pt-0 print:pb-0 print:px-0 print:bg-white" role="main" aria-label="Resume">
        <div className="max-w-5xl mx-auto">
          {/* Header with Back Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 md:mb-8 print:hidden">
            <Button 
              asChild
              variant="ghost" 
              className="brutal-btn-ghost w-full sm:w-auto"
            >
              <Link to="/About">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to About
              </Link>
            </Button>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <Button
                asChild
                className="w-full brutal-btn px-4 py-2 font-medium flex items-center justify-center cursor-pointer sm:w-auto relative z-10"
              >
                <a href={CV_PDF_URL} download={CV_PDF_DOWNLOAD_NAME}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="w-full brutal-btn-ghost px-4 py-2 font-medium flex items-center justify-center cursor-pointer sm:w-auto relative z-10"
              >
                <a href={CV_ATS_DOCX_URL} download="Dixon-Bordiano-CV-ATS.docx">
                  <FileDoc className="w-4 h-4 mr-2" />
                  ATS .docx
                </a>
              </Button>
            </div>
          </div>

          {/* Resume Content */}
          <Card className="brutal-panel p-4 md:p-8 lg:p-12 print:bg-white print:border-0 print:shadow-none print:text-black" data-resume-content>
            {/* Header Section */}
            <div className="text-center mb-8 md:mb-12 border-b border-[var(--brutal-ink)] pb-6 md:pb-8">
              <div className="flex items-center justify-center mb-4 md:mb-6" data-print-hide>
                <Avatar className="w-16 h-16 md:w-24 md:h-24 ring-4 ring-[var(--brutal-ink)]">
                  <AvatarImage src="https://wemxiqpkiyrwpffrdrgu.supabase.co/storage/v1/object/public/public-assets/projects/c0329852-5824-4c56-b2fb-026919af3589/a449770d-d689-4eef-b810-94ffbacd1921.jpeg" alt="Dixon Bordiano" className="object-cover" />
                  <AvatarFallback className="text-xl md:text-2xl bg-[var(--brutal-ink)] text-[var(--brutal-paper)]">DB</AvatarFallback>
                </Avatar>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-[var(--brutal-ink)] mb-2 md:mb-3">Dixon Bordiano</h1>
              <h2 className="text-lg md:text-2xl text-[var(--brutal-ink)] mb-4 px-2">UX &amp; Product Design Leader | AI-Powered SaaS | Design Systems</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center lg:justify-center gap-3 lg:space-x-8 lg:gap-0 text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                <div className="flex items-center justify-center lg:justify-start">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Porto, Portugal</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start">
                  <Envelope className="w-4 h-4 mr-2 flex-shrink-0" />
                  <a href="mailto:dbordiano@gmail.com" className="truncate hover:underline">dbordiano@gmail.com</a>
                </div>
                <div className="flex items-center justify-center lg:justify-start">
                  <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>+351 910 733 418</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start">
                  <LinkedinLogo className="w-4 h-4 mr-2 flex-shrink-0" />
                  <a href="https://www.linkedin.com/in/dixonbordiano/" target="_blank" rel="noopener noreferrer" className="hover:underline">linkedin.com/in/dixonbordiano</a>
                </div>
              </div>
              <div data-print-only>
                <p>Porto, Portugal | dbordiano@gmail.com | +351 910 733 418</p>
                <p>LinkedIn: https://www.linkedin.com/in/dixonbordiano</p>
                <p>Portfolio: https://www.dixonbordiano.com</p>
              </div>
            </div>

            {/* Professional Summary */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-3 md:mb-4 flex items-center print:text-black">
                <Medal weight="duotone" className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-[var(--brutal-ink)] flex-shrink-0" data-print-hide />
                Professional Summary
              </h3>
              <p className="text-[var(--brutal-ink)] leading-relaxed text-base md:text-lg print:text-black">
                Design leader with 12+ years driving product-led growth in AI-powered enterprise SaaS. Scaled teams and practices that generated $6M+ ARR and expanded client bases by 400%. Founded Cognira&apos;s first in-house Product Design &amp; UX practice across 3 continents; launched an accessibility-first design system that cut regression bugs by 44%. Co-Founder at URSO Invencivel (consulting, workshops, education). Seeking Head of Design / Director of UX / Lead Product Design roles (full-time, part-time, or fractional), preferably remote.
              </p>
            </section>

            {/* Key Achievements */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 flex items-center print:text-black">
                <Medal weight="duotone" className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-[var(--brutal-ink)] flex-shrink-0" data-print-hide />
                Key Achievements
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6" data-print-hide>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">$6M+ New ARR</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Established Portugal UX office and grew client base by 400% through design-led SaaS work</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">44% Reduction in Regression Bugs</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Built Storybook code framework and integrated design system with engineering</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">Employee of the Year (2017)</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Recognized at Oracle for leading cross-product UX unification</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">50% Faster Usability Fixes</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Improved design system integration and processes, accelerating time to market</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">Founded First UX Practice</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Established Cognira&apos;s inaugural in-house Product Design &amp; UX practice</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[var(--brutal-ink)] rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                    <div>
                      <h4 className="font-semibold text-[var(--brutal-ink)]">60% Reduction in Training Time</h4>
                      <p className="text-[var(--brutal-muted)] text-sm">Streamlined user onboarding and reduced help desk tickets through improved UX</p>
                    </div>
                  </div>
                </div>
              </div>
              <ul data-print-only>
                <li>$6M+ New ARR: Established Portugal UX office and grew client base by 400% through design-led SaaS work</li>
                <li>44% Reduction in Regression Bugs: Built Storybook code framework and integrated design system with engineering</li>
                <li>Employee of the Year (2017): Recognized at Oracle for leading cross-product UX unification</li>
                <li>50% Faster Usability Fixes: Improved design system integration and processes, accelerating time to market</li>
                <li>Founded First UX Practice: Established Cognira&apos;s inaugural in-house Product Design &amp; UX practice</li>
                <li>60% Reduction in Training Time: Streamlined user onboarding and reduced help desk tickets through improved UX</li>
              </ul>
            </section>

            {/* Professional Experience */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 flex items-center print:text-black">
                <Briefcase className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-[var(--brutal-ink)] flex-shrink-0" data-print-hide />
                Professional Experience
              </h3>
              <div className="space-y-6 md:space-y-8">
                <div data-resume-job className="border-l-4 border-slate-700 pl-4 md:pl-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2 print:flex-row print:items-baseline print:justify-between print:gap-3">
                    <div className="flex-1">
                      <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">Co-Founder</h4>
                      <p className="text-base md:text-lg text-[var(--brutal-ink)] font-semibold" data-print-hide>
                        <a href="https://ursoinvencivel.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 underline underline-offset-2">
                          URSO Invencivel
                        </a>
                      </p>
                      <p data-print-only>URSO Invencivel | Porto, Portugal | March 2026 – Present</p>
                    </div>
                    <div className="text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span>Mar 2026 – Present</span>
                      </div>
                    </div>
                  </div>
                  <ul className="text-[var(--brutal-ink)] space-y-2 list-disc list-outside ml-4 print:text-black">
                    <li data-print-hide>
                      Co-founded{' '}
                      <a href="https://ursoinvencivel.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[var(--brutal-ink)]">
                        URSO Invencivel
                      </a>
                      {' '}with a focus on consulting, workshops, and education
                    </li>
                    <li data-print-only>Co-founded URSO Invencivel (https://ursoinvencivel.com) focused on consulting, workshops, and education</li>
                    <li>Design and facilitate workshops that turn complex topics into practical, repeatable practices</li>
                    <li>Advise clients and collaborators through consulting—strategy, process, and decision-making</li>
                    <li>Build education programs and learning materials people can apply in everyday contexts</li>
                    <li>Shape services around clear facilitation: teach, guide, and leave people more capable than when they arrived</li>
                  </ul>
                </div>

                <div data-resume-job className="border-l-4 border-slate-600 pl-4 md:pl-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2 print:flex-row print:items-baseline print:justify-between print:gap-3">
                    <div className="flex-1">
                      <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">Director of UX</h4>
                      <p className="text-base md:text-lg text-[var(--brutal-ink)] font-semibold" data-print-hide>Cognira</p>
                      <p data-print-only>Cognira | Porto, Portugal | March 2023 – January 2026</p>
                    </div>
                    <div className="text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span>Mar 2023 – Jan 2026</span>
                      </div>
                    </div>
                  </div>
                  <ul className="text-[var(--brutal-ink)] space-y-2 list-disc list-outside ml-4 print:text-black">
                    <li>Founded and scaled Cognira&apos;s first in-house Product Design &amp; UX practice for PromoAI SaaS (0 to 8 PMs, designers, and researchers across 3 continents in 18 months)</li>
                    <li>Expanded client base by 400% and generated $6M+ in new ARR through design-led SaaS migration</li>
                    <li>Launched accessibility-first design system (Figma + Storybook); cut regression bugs 44% and increased sprint velocity by 2.5 story points</li>
                    <li>Introduced usability-bug process; usability issues fell by half; remaining issues resolved twice as fast</li>
                    <li>Reduced training time and navigation-related support tickets by 60% via onboarding redesign</li>
                    <li>Directed UX strategy for AI forecasting features, responsible AI principles, and user-trust metrics</li>
                  </ul>
                </div>

                <div data-resume-job className="border-l-4 border-stone-500 pl-4 md:pl-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2 print:flex-row print:items-baseline print:justify-between print:gap-3">
                    <div className="flex-1">
                      <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">Senior UX Designer</h4>
                      <p className="text-base md:text-lg text-[var(--brutal-ink)] font-semibold" data-print-hide>Oracle</p>
                      <p data-print-only>Oracle | Lisbon, Portugal | March 2015 – March 2023</p>
                    </div>
                    <div className="text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span>Mar 2015 – Mar 2023</span>
                      </div>
                    </div>
                  </div>
                  <ul className="text-[var(--brutal-ink)] space-y-2 list-disc list-outside ml-2 md:ml-4 text-sm md:text-base print:text-black">
                    <li>Led multi-team UX initiatives across planning, supply chain, store operations, and analytics products</li>
                    <li>Embedded user research and usability testing into agile workflows with product and engineering teams</li>
                    <li>Standardized design patterns, reducing UI development time and improving cross-solution consistency</li>
                    <li>Recognized as Employee of the Year (2017) for leading cross-product UX unification</li>
                    <li>Mentored junior designers and established UX best practices across multiple product teams</li>
                  </ul>
                </div>

                <div data-resume-job className="border-l-4 border-stone-400 pl-4 md:pl-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2 print:flex-row print:items-baseline print:justify-between print:gap-3">
                    <div className="flex-1">
                      <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">UX Designer</h4>
                      <p className="text-base md:text-lg text-[var(--brutal-ink)] font-semibold" data-print-hide>Digital River</p>
                      <p data-print-only>Digital River | Minneapolis, USA | June 2014 – March 2015</p>
                    </div>
                    <div className="text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span>Jun 2014 – Mar 2015</span>
                      </div>
                    </div>
                  </div>
                  <ul className="text-[var(--brutal-ink)] space-y-2 list-disc list-outside ml-2 md:ml-4 text-sm md:text-base print:text-black">
                    <li>Conducted user research and created interaction patterns for e-commerce applications</li>
                    <li>Designed enterprise mobile point-of-sale, customer service tools, and checkout flows</li>
                    <li>Collaborated with development teams to implement responsive design solutions</li>
                    <li>Performed usability testing and iterative design improvements based on user feedback</li>
                  </ul>
                </div>

                <div data-resume-job className="border-l-4 border-[var(--brutal-ink)] pl-4 md:pl-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2 print:flex-row print:items-baseline print:justify-between print:gap-3">
                    <div className="flex-1">
                      <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">Earlier Roles</h4>
                      <p className="text-base md:text-lg text-[var(--brutal-muted)] font-semibold" data-print-hide>UX Design Trainee, QA Engineer, Web Designer</p>
                      <p data-print-only>UX Design Trainee, QA &amp; Automation Engineer, Web Designer | 2010 – 2014</p>
                    </div>
                    <div className="text-[var(--brutal-muted)] text-sm md:text-base" data-print-hide>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span>2010 – 2014</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[var(--brutal-ink)] text-sm md:text-base print:text-black">Led QA teams, built automation pipelines, and contributed to early design and testing initiatives.</p>
                </div>
              </div>
            </section>

            {/* Skills & Expertise */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 print:text-black">
                <span data-print-hide>Core Skills &amp; Expertise</span>
                <span data-print-only>Core Skills</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-print-hide>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Design Leadership</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Strategic Vision</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Team Management</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Design Culture</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Mentorship</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Practice Building</Badge>
                  </div>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">UX &amp; Research</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">User Research</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Usability Testing</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Information Architecture</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Prototyping</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Design Systems</Badge>
                  </div>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)] mb-3">Product &amp; Strategy</h4>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Product Strategy</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Roadmap Planning</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Consulting</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Workshops</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Facilitation</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">AI/ML Design</Badge>
                    <Badge className="bg-stone-200/80 text-[var(--brutal-ink)] border-[var(--brutal-ink)] text-xs md:text-sm">Enterprise UX</Badge>
                  </div>
                </div>
              </div>
              <div data-print-only>
                <p>Design Leadership: Strategic Vision, Team Management, Design Culture, Mentorship, Practice Building, ProductOps, DesignOps, Stakeholder Alignment</p>
                <p>UX &amp; Research: User Research, Usability Testing, Information Architecture, Prototyping, Design Systems, Accessibility, Continuous Discovery</p>
                <p>Product &amp; Strategy: Product Strategy, Roadmap Planning, Consulting, Workshops, Facilitation, AI/ML Design, Enterprise UX, Responsible AI</p>
                <p>Tools: Figma, FigJam, Storybook, Jira, Confluence, Cursor</p>
              </div>
            </section>

            {/* Languages */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 print:text-black">Languages</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-print-hide>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)]">English</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Native</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)]">Portuguese</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Conversational</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-[var(--brutal-panel)] rounded-full flex-shrink-0" aria-hidden="true"></div>
                  <div>
                    <h4 className="text-base md:text-lg font-semibold text-[var(--brutal-ink)]">Romanian</h4>
                    <p className="text-[var(--brutal-muted)] text-sm">Conversational</p>
                  </div>
                </div>
              </div>
              <p data-print-only>English (Native) | Portuguese (Conversational) | Romanian (Conversational)</p>
            </section>

            {/* Education & Certifications */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 flex items-center print:text-black">
                <GraduationCap className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-[var(--brutal-ink)] flex-shrink-0" data-print-hide />
                <span data-print-hide>Education &amp; Certifications</span>
                <span data-print-only>Education</span>
              </h3>
              <div className="space-y-6 md:space-y-8">
                <div data-resume-block className="border-l-4 border-stone-500 pl-4 md:pl-6">
                  <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black">Bachelor of Arts, Studio Art (Digital Art)</h4>
                  <p className="text-base md:text-lg text-[var(--brutal-ink)] font-semibold print:text-black">University of Minnesota Morris | 2010</p>
                  <p className="text-[var(--brutal-ink)] mt-2 text-sm md:text-base" data-print-hide>Foundation in visual design, composition, and creative problem-solving that formed the basis for a career in user experience design.</p>
                </div>
                
                <div data-resume-block className="border-l-4 border-slate-600 pl-4 md:pl-6">
                  <h3 data-print-only>Certifications</h3>
                  <h4 className="text-lg md:text-xl font-bold text-[var(--brutal-ink)] print:text-black" data-print-hide>Professional Certifications</h4>
                  <ul className="text-[var(--brutal-ink)] space-y-1 list-disc list-outside ml-4 mt-2 print:text-black text-sm md:text-base">
                    <li>ISTQB Certified Foundation Level Test Professional (2012)</li>
                    <li>Oracle JavaScript Extension Toolkit (JET) Certification (2016)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Awards & Recognition */}
            <section className="mb-6 md:mb-10">
              <h3 className="text-xl md:text-2xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 flex items-center print:text-black">
                <Medal weight="duotone" className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3 text-[var(--brutal-ink)] flex-shrink-0" data-print-hide />
                <span data-print-hide>Awards &amp; Recognition</span>
                <span data-print-only>Awards</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-print-hide>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-200 border border-[var(--brutal-ink)] rounded-full flex items-center justify-center flex-shrink-0">
                    <Medal weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">Employee of the Year</h4>
                    <p className="text-[var(--brutal-muted)]">Oracle • 2017</p>
                    <p className="text-[var(--brutal-ink)] text-sm mt-1">Recognized for leading cross-product UX unification and outstanding contribution to enterprise design excellence.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-stone-200 border border-[var(--brutal-ink)] rounded-full flex items-center justify-center flex-shrink-0">
                    <Medal weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[var(--brutal-ink)]">CIO Star Award</h4>
                    <p className="text-[var(--brutal-muted)]">Digital River • 2012</p>
                    <p className="text-[var(--brutal-ink)] text-sm mt-1">Early career recognition for contributions to QA automation and testing initiatives.</p>
                  </div>
                </div>
              </div>
              <ul data-print-only>
                <li>Employee of the Year — Oracle (2017)</li>
                <li>CIO Star Award — Digital River (2012)</li>
              </ul>
            </section>
          </Card>
        </div>
      </main>
    </Components.PageContainer>
    </>
  )
}